import { Component, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { DeviceService } from '../../shared/services/device.service';
import { ETipoPermiso } from '@core/enums/tipoPermiso.enum';
import { PermisoService } from '@shared/services/permiso.service';
import { IConfigEmpresaRes } from '@core/interfaces/request-response/empresa.dto.interface';
import { EmpresaService } from '@shared/services/empresa.service';
import { AuthApiService } from '@core/services/api/auth.service';
import { ETipoSistemasEmpresa } from '@core/enums/tipoSistemasEmpresa.enum';

interface MenuItemBase {
  id: string;
  title: string;
  icon: string;
  permisos: ETipoPermiso[];
  hidden?: boolean;
}

interface SimpleMenuItem extends MenuItemBase {
  type: 'link';
  route: string;
}

interface GroupMenuItem extends MenuItemBase {
  type: 'group';
  items: MenuItem[];
}

type MenuItem = SimpleMenuItem | GroupMenuItem;

@Component({
  selector: 'app-sidebar',
  imports: [RouterModule],
  templateUrl: './sidebar.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './sidebar.css',
})
export class Sidebar implements OnInit {
  collapsed = signal(false);
  expandedGroups = signal<Set<string>>(new Set());
  isMobile = signal(false);

  permisos = signal<ETipoPermiso[]>([]);

  empresaConfig: IConfigEmpresaRes | null = null;

  constructor(private deviceService: DeviceService) {
    // Inicializar el estado de mobile al cargar
    this.updateMobileState();

    // Escuchar cambios de tamaño de ventana
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', () => {
        this.updateMobileState();
      });
    }
  }
  private readonly permisoService = inject(PermisoService);
  private readonly empresaService = inject(EmpresaService);
  private readonly authApiService = inject(AuthApiService);
  private readonly router = inject(Router);
  ngOnInit(): void {
    this.permisoService.permisos$.subscribe((permisos) => {
      this.permisos.set(permisos);
    });
    this.empresaService.empresaConfigData$.subscribe((empresa) => {
      this.empresaConfig = empresa;
    });
  }

  private updateMobileState(): void {
    this.isMobile.set(!this.deviceService.isDesktop());
  }

  get menuItems(): MenuItem[] {
    return [
      {
        id: 'dashboard',
        type: 'link',
        title: 'Dashboard',
        icon: 'bi-columns-gap',
        route: '/panel/dashboard',
        permisos: [
          ETipoPermiso.ADMINISTRADOR,
          ETipoPermiso.MOVIMIENTO_CAJA_OBTENER_REPORTE,
          ETipoPermiso.VENTA_OBTENER_REPORTE,
          ETipoPermiso.CAJA_TURNO_OBTENER_REPORTE,
          ETipoPermiso.KARDEX_OBTENER_REPORTE,
        ],
      },
      {
        id: 'operations',
        type: 'group',
        title: 'Sistemas',
        icon: 'bi-clipboard-check',
        permisos: [ETipoPermiso.VENDEDOR],
        items: [
          {
            id: 'pos',
            type: 'link',
            title: 'Punto de Venta',
            icon: 'bi-cart4',
            route: '/panel/pos',
            permisos: [ETipoPermiso.VENDEDOR],
            hidden: !this.empresaConfig?.sistemas_activos.includes(ETipoSistemasEmpresa.POS),
          },
          {
            id: 'comandas',
            type: 'link',
            title: 'Gestión de Comandas',
            icon: 'bi-list',
            route: '/panel/comandas',
            permisos: [ETipoPermiso.VENDEDOR],
            hidden: !this.empresaConfig?.sistemas_activos.includes(ETipoSistemasEmpresa.COMANDAS),
          },
          {
            id: 'tiempo',
            type: 'link',
            title: 'Gestión de Tiempo',
            icon: 'bi-clock-history',
            route: '/panel/gestion-tiempo',
            permisos: [ETipoPermiso.VENDEDOR],
            hidden: !this.empresaConfig?.sistemas_activos.includes(ETipoSistemasEmpresa.CONTROLADOR_TIEMPO),
          },
          {
            id: 'mica-chat',
            type: 'link',
            title: 'Mica Chat',
            icon: 'bi-chat-dots',
            route: '/panel/mica-chat',
            permisos: [ETipoPermiso.VENDEDOR],
            hidden: !this.empresaConfig?.sistemas_activos.includes(ETipoSistemasEmpresa.MICA_CHAT),
          },
          {
            id: 'facturacion',
            type: 'link',
            title: 'Facturación',
            icon: 'bi-receipt',
            route: '/panel/facturacion',
            permisos: [ETipoPermiso.VENDEDOR],
            hidden: !this.empresaConfig?.sistemas_activos.includes(ETipoSistemasEmpresa.FACTURACION),
          },
          {
            id: 'reservas',
            type: 'link',
            title: 'Reservas',
            icon: 'bi-calendar-check',
            route: '/panel/reservas',
            permisos: [ETipoPermiso.VENDEDOR],
            hidden: !this.empresaConfig?.sistemas_activos.includes(ETipoSistemasEmpresa.RESERVAS),
          },
        ],
      },
      {
        id: 'management',
        type: 'group',
        title: 'Gestión',
        icon: 'bi-building',
        permisos: [
          ETipoPermiso.ADMINISTRADOR,
          ETipoPermiso.PRODUCTO_VER_LISTA,
          ETipoPermiso.PRODUCTO_CREAR_INGRESO,
          ETipoPermiso.EMPLEADO_VER_LISTA,
          ETipoPermiso.CATEGORIA_VER_LISTA,
          ETipoPermiso.VENTA_VER_LISTA,
          ETipoPermiso.TIPO_PAGO_VER_LISTA,
          ETipoPermiso.CLIENTE_VER_LISTA,
        ],
        items: [
          {
            id: 'productos',
            type: 'group',
            title: 'Productos',
            icon: 'bi-box',
            permisos: [ETipoPermiso.PRODUCTO_VER_LISTA],
            items: [
              {
                id: 'lista-productos',
                type: 'link',
                title: 'Lista de Productos',
                icon: 'bi-box-seam',
                route: '/panel/gestion/producto',
                permisos: [ETipoPermiso.PRODUCTO_VER_LISTA],
              },
              {
                id: 'ingresar-inventario',
                type: 'link',
                title: 'Agregar Inventario',
                icon: 'bi-plus-square',
                route: '/panel/gestion/agregar-inventario',
                permisos: [ETipoPermiso.PRODUCTO_CREAR_INGRESO],
              },
            ],
          },
          {
            id: 'recetas',
            type: 'link',
            title: 'Recetas (Beta)',
            icon: 'bi-journal-bookmark',
            route: '/panel/gestion/recetas',
            permisos: [ETipoPermiso.ADMINISTRADOR],
          },
          {
            id: 'campanas',
            type: 'link',
            title: 'Campañas',
            icon: 'bi-megaphone',
            route: '/panel/gestion/campanas',
            permisos: [ETipoPermiso.VENDEDOR],
          },
          {
            id: 'empleados',
            type: 'group',
            title: 'Empleados',
            icon: 'bi-people',
            permisos: [ETipoPermiso.EMPLEADO_VER_LISTA],
            items: [
              {
                id: 'lista-empleados',
                type: 'link',
                title: 'Lista de Empleados',
                icon: 'bi-people',
                route: '/panel/gestion/empleado',
                permisos: [ETipoPermiso.EMPLEADO_VER_LISTA],
              },
              {
                id: 'sesiones',
                type: 'link',
                title: 'Sesiones de empleados',
                icon: 'bi-shield-shaded',
                route: '/panel/gestion/sesiones',
                permisos: [ETipoPermiso.ADMINISTRADOR, ETipoPermiso.SESIONES_VER],
              },
            ],
          },
          {
            id: 'clientes',
            type: 'link',
            title: 'Clientes',
            icon: 'bi-person-lines-fill',
            route: '/panel/gestion/cliente',
            permisos: [ETipoPermiso.CLIENTE_VER_LISTA],
          },
          {
            id: 'categorias',
            type: 'link',
            title: 'Categorías',
            icon: 'bi-tags',
            route: '/panel/gestion/categoria',
            permisos: [ETipoPermiso.CATEGORIA_VER_LISTA],
          },
          {
            id: 'finance',
            type: 'group',
            title: 'Finanzas',
            icon: 'bi-cash-stack',
            permisos: [ETipoPermiso.TIPO_PAGO_VER_LISTA, ETipoPermiso.VENTA_VER_LISTA],
            items: [
              {
                id: 'pagos',
                type: 'link',
                title: 'Tipo de Pagos',
                icon: 'bi-credit-card-2-front-fill',
                route: '/panel/gestion/pagos',
                permisos: [ETipoPermiso.TIPO_PAGO_VER_LISTA],
              },
              {
                id: 'creditos',
                type: 'link',
                title: 'Clientes con créditos',
                icon: 'bi-credit-card',
                route: '/panel/gestion/creditos',
                permisos: [ETipoPermiso.CLIENTE_VER_LISTA],
              },
            ],
          },
        ],
      },
      {
        id: 'reportes',
        type: 'group',
        title: 'Reportes',
        icon: 'bi-bar-chart-line-fill',
        permisos: [
          ETipoPermiso.MOVIMIENTO_CAJA_OBTENER_REPORTE,
          ETipoPermiso.VENTA_OBTENER_REPORTE,
          ETipoPermiso.CAJA_TURNO_OBTENER_REPORTE,
          ETipoPermiso.KARDEX_OBTENER_REPORTE,
        ],
        items: [
          {
            id: 'reporte-caja',
            type: 'link',
            title: 'Reporte de Caja',
            icon: 'bi-cash-coin',
            route: '/panel/gestion/reporte/caja',
            permisos: [ETipoPermiso.MOVIMIENTO_CAJA_OBTENER_REPORTE],
          },
          {
            id: 'reporte-movimiento-caja',
            type: 'link',
            title: 'Movimiento de Caja',
            icon: 'bi-journal-text',
            route: '/panel/gestion/reporte/movimiento-caja',
            permisos: [ETipoPermiso.CAJA_TURNO_OBTENER_REPORTE],
          },
          {
            id: 'reporte-venta',
            type: 'link',
            title: 'Reporte de Venta',
            icon: 'bi-bar-chart',
            route: '/panel/gestion/reporte/venta',
            permisos: [ETipoPermiso.VENTA_OBTENER_REPORTE],
          },
          {
            id: 'reporte-kardex',
            type: 'link',
            title: 'Reporte de Kardex',
            icon: 'bi-journal',
            route: '/panel/gestion/reporte/kardex',
            permisos: [ETipoPermiso.KARDEX_OBTENER_REPORTE],
          },
        ],
      },
    ];
  }

  get configMenuItem(): GroupMenuItem {
    return {
      id: 'config',
      type: 'group',
      title: 'Configuración',
      icon: 'bi-gear',
      permisos: [
        ETipoPermiso.ADMINISTRADOR,
        ETipoPermiso.EMPRESA_EDITAR,
        ETipoPermiso.SUCURSAL_EDITAR,
      ],
      items: [
        {
          id: 'config-empresa',
          type: 'link',
          title: 'Config Empresa',
          icon: 'bi-building',
          route: '/panel/config/empresa',
          permisos: [ETipoPermiso.EMPRESA_EDITAR],
        },
        {
          id: 'config-sucursal',
          type: 'link',
          title: 'Config Sucursal',
          icon: 'bi-shop',
          route: '/panel/config/sucursal',
          permisos: [ETipoPermiso.SUCURSAL_EDITAR],
        },
        {
          id: 'config-pidelo',
          type: 'link',
          title: 'Pidelo',
          icon: 'bi-phone',
          route: '/panel/config/pidelo',
          permisos: [ETipoPermiso.EMPRESA_EDITAR],
          hidden: !this.empresaConfig?.sistemas_activos.includes(ETipoSistemasEmpresa.PIDELO),
        },
        {
          id: 'config-punto-impresion',
          type: 'link',
          title: 'Puntos de Impresión',
          icon: 'bi-printer',
          route: '/panel/config/punto-impresion',
          permisos: [ETipoPermiso.ADMINISTRADOR],
        },
        {
          id: 'config-promocion-impresora',
          type: 'link',
          title: 'Config Promoción Impresora',
          icon: 'bi-megaphone',
          route: '/panel/config/promociones-impresora',
          permisos: [ETipoPermiso.ADMINISTRADOR],
        },
        {
          id: 'seguridad',
          type: 'link',
          title: 'Seguridad',
          icon: 'bi-shield-lock',
          route: '/panel/config/seguridad/sesiones',
          permisos: [ETipoPermiso.ADMINISTRADOR, ETipoPermiso.SESIONES_VER],
        },
      ],
    };
  }
  /**
   * Verifica si el usuario actual tiene permisos para acceder a un item del menú
   * @param item MenuItem con permisos requeridos
   * @returns true si el usuario tiene al menos uno de los permisos requeridos o es administrador
   */
  tienePermiso(item: MenuItem): boolean {
    // Dependencia reactiva: al cambiar permisos(), el template reevalúa este método.
    this.permisos();
    return this.permisoService.tieneAlgunPermiso(item.permisos);
  }
  toggleSidebar(valor?: boolean) {
    if (valor !== undefined) {
      this.collapsed.set(valor);
      return;
    }
    this.collapsed.update((v) => !v);
  }

  toggleGroup(groupId: string) {
    this.expandedGroups.update((groups) => {
      if (this.collapsed()) {
        this.toggleSidebar(false);
      }
      const newGroups = new Set(groups);
      if (newGroups.has(groupId)) {
        newGroups.delete(groupId);
      } else {
        newGroups.add(groupId);
      }
      return newGroups;
    });
  }

  isGroupExpanded(groupId: string): boolean {
    return this.expandedGroups().has(groupId);
  }

  isGroup(item: MenuItem): item is GroupMenuItem {
    return item.type === 'group';
  }

  isLink(item: MenuItem): item is SimpleMenuItem {
    return item.type === 'link';
  }

  onCerrarSesion() {
    this.authApiService.logout().subscribe({
      complete: () => {
        this.router.navigate(['/home']);
      },
    });
  }
}
